(() => {
  if (customElements.get('dyld-cart-extras')) return;

  const normalize = (code) => String(code || '').trim().toUpperCase();
  const boosterCatalog = (drawer) => JSON.parse(drawer.querySelector('[data-dyld-booster-catalog]')?.textContent || '[]');
  const codesFor = (cart) => {
    const codes = new Set((cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => normalize(entry.code)));
    const applications = [...(cart.cart_level_discount_applications || [])];
    (cart.items || []).forEach((item) => (item.line_level_discount_allocations || []).forEach((allocation) => applications.push(allocation.discount_application)));
    applications.filter((entry) => entry?.type === 'discount_code').forEach((entry) => codes.add(normalize(entry.title)));
    return [...codes].filter(Boolean);
  };

  class DyldCartExtras extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.onClick = (event) => {
        const boost = event.target.closest('[data-boost-variant]');
        const code = event.target.closest('[data-remove-code]');
        if (boost) this.changeBoost(boost);
        if (code) this.changeDiscount(code.dataset.removeCode, true);
      };
      this.onSubmit = (event) => {
        if (!event.target.matches('[data-discount-form]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.changeDiscount(this.querySelector('[name="discount"]').value);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.formatFrame = requestAnimationFrame(() => this.formatEntries());
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
        if (Array.isArray(cartData?.discount_codes)) this.renderCodes(codesFor(cartData));
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      cancelAnimationFrame(this.formatFrame);
      this.unsubscribe?.();
      // A section render disconnects this element during an intentional request.
      // Do not abort cart mutations: the server might already have applied them.
    }

    formatEntries() {
      const total = this.drawer?.querySelector('[data-total-entries]');
      if (!total) return;
      const count = total.querySelector('strong');
      const number = Number(total.dataset.totalEntries);
      if (count && Number.isFinite(number)) count.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en').format(number);
    }

    lock() {
      if (!this.drawer || this.drawer.hasAttribute('data-dyld-busy') || this.drawer.querySelector('.cart__items--disabled')) return false;
      this.drawer.setAttribute('data-dyld-busy', '');
      this.drawer.setAttribute('aria-busy', 'true');
      this.locked = [...this.drawer.querySelectorAll('button:not(.drawer__close), input, textarea')].filter((control) => !control.disabled);
      this.locked.forEach((control) => { control.disabled = true; });
      return true;
    }

    unlock() {
      this.locked?.forEach((control) => { if (control.isConnected) control.disabled = false; });
      this.drawer.removeAttribute('data-dyld-busy');
      this.drawer.removeAttribute('aria-busy');
    }

    async request(path, body) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}${path}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : {}),
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400 || data.errors) {
        throw new Error(typeof data.description === 'string' ? data.description : 'The cart could not be updated. Please try again.');
      }
      return data;
    }

    sections() {
      return { sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname };
    }

    async refresh(cart, focusId) {
      if (!cart.sections?.['cart-drawer'] || !cart.sections?.['cart-icon-bubble']) {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Your cart changed, but could not refresh. Reload to see the latest total.');
        cart.sections = await response.json();
      }
      const html = new DOMParser().parseFromString(cart.sections['cart-drawer'] || '', 'text/html');
      const inner = html.querySelector('.drawer__inner');
      const bubble = new DOMParser().parseFromString(cart.sections['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
      if (!inner || !bubble) throw new Error('Your cart changed, but could not refresh. Reload to see the latest total.');
      this.drawer.querySelector('.drawer__inner').replaceWith(inner);
      this.drawer.classList.toggle('is-empty', cart.item_count === 0);
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) icon.innerHTML = bubble.innerHTML;
      this.unlock();
      const extras = this.drawer.querySelector('dyld-cart-extras');
      extras?.formatEntries();
      extras?.renderCodes(codesFor(cart));
      if (this.drawer.classList.contains('active')) {
        const focus = this.drawer.querySelector(`#${focusId}`) || this.drawer.querySelector('.drawer__close');
        trapFocus(this.drawer.querySelector('.drawer__inner'), focus);
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dyld-cart-extras', cartData: cart });
    }

    renderCodes(codes) {
      const container = this.querySelector('.dyld-cart-discount__codes');
      if (!container) return;
      const buttons = codes.map((code) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dyld-cart-code';
        button.dataset.removeCode = code;
        button.setAttribute('aria-label', `Remove discount code ${code}`);
        button.append(document.createTextNode(`${code} `));
        const cross = document.createElement('span');
        cross.setAttribute('aria-hidden', 'true');
        cross.textContent = '×';
        button.append(cross);
        return button;
      });
      container.replaceChildren(...buttons);
    }

    message(id, text, error = false) {
      const target = this.drawer.querySelector(`#${id}`);
      if (!target) return;
      target.dataset.error = String(error);
      target.textContent = text;
    }

    async changeDiscount(value, remove = false) {
      const code = normalize(value);
      if (!code || code.includes(',')) {
        this.message('DyldCartCodeStatus', 'Enter one discount code at a time.', true);
        return;
      }
      if (!this.lock()) return;
      this.message('DyldCartCodeStatus', remove ? 'Removing code…' : 'Checking code…');
      let changed = false;
      try {
        const current = await this.request('cart.js');
        const existing = codesFor(current);
        if (!remove && existing.includes(code)) {
          this.message('DyldCartCodeStatus', `${code} is already applied.`);
          return;
        }
        const codes = remove ? existing.filter((entry) => entry !== code) : [...existing, code];
        const updated = await this.request('cart/update.js', { discount: codes.join(','), ...this.sections() });
        changed = true;
        const accepted = codesFor(updated).includes(code);
        await this.refresh(updated, 'DyldCartDiscount');
        const input = this.drawer.querySelector('#DyldCartDiscount');
        if (!remove && !accepted && input) {
          input.value = value;
          input.setAttribute('aria-invalid', 'true');
        }
        this.message('DyldCartCodeStatus', remove ? `${code} removed.` : accepted ? `${code} applied. Any bonus entries are confirmed after checkout.` : 'This code is invalid or does not apply to this cart. Check its conditions and try again.', !remove && !accepted);
      } catch (error) {
        try { await this.refresh(await this.request('cart.js'), 'DyldCartDiscount'); } catch (_) { /* Keep the original failure visible. */ }
        this.message('DyldCartCodeStatus', changed ? 'Your code was sent, but the cart could not refresh. Reload to check its status.' : error.message, true);
      } finally {
        this.unlock();
      }
    }

    async changeBoost(button, { selectOnly = false } = {}) {
      const variantId = Number(button.dataset.boostVariant);
      if (!variantId || !this.lock()) return false;
      let remove = !selectOnly && button.getAttribute('aria-pressed') === 'true';
      const productIds = new Set(boosterCatalog(this.drawer).map((entry) => entry.product));
      this.message('DyldCartBoostStatus', 'Updating your multiplier…');
      let changed = false;
      try {
        let cart = await this.request('cart.js');
        const boosterQuantity = cart.items.reduce((total, item) => total + (productIds.has(item.product_id) ? item.quantity : 0), 0);
        // An older invalid cart uses selection to keep this tier, rather than removing every tier.
        if (boosterQuantity > 1) remove = false;
        const selected = cart.items.find((item) => item.variant_id === variantId);
        // Add first: a sold-out replacement must never remove the existing booster.
        if (!remove && !selected) {
          await this.request('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
          changed = true;
          cart = await this.request('cart.js');
        }
        const updates = {};
        // Keep one line, not every line sharing the same variant (properties can split a variant).
        const keep = remove ? null : cart.items.find((item) => item.variant_id === variantId);
        cart.items.forEach((item) => {
          if (productIds.has(item.product_id)) updates[item.key] = item.key === keep?.key ? 1 : 0;
        });
        cart = await this.request('cart/update.js', { updates, ...this.sections() });
        changed = true;
        await this.refresh(cart, button.id);
        this.message('DyldCartBoostStatus', remove ? 'Cart multiplier removed. Your entry total is updated.' : 'Cart multiplier added. Your entry total is updated.');
        return true;
      } catch (error) {
        try { await this.refresh(await this.request('cart.js'), button.id); } catch (_) { /* Keep the error visible if rendering also fails. */ }
        this.lastError = changed ? 'The multiplier change was not completed. Select the multiplier you want to keep before checkout.' : error.message;
        this.message('DyldCartBoostStatus', this.lastError, true);
        return false;
      } finally {
        this.unlock();
      }
    }
  }
  customElements.define('dyld-cart-extras', DyldCartExtras);

  // Capture only cart-booster submissions, before native/product-form/app handlers add a second line.
  // The same transaction is used on collections, product pages, and repeated additions.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !new URL(form.action).pathname.endsWith('/cart/add')) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    const variantId = Number(new FormData(form).get('id'));
    if (!boosterCatalog(drawer).some((entry) => entry.variant === variantId)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (drawer.hasAttribute('data-dyld-busy')) return;
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter?.disabled) return;
    submitter?.setAttribute('aria-busy', 'true');
    drawer.setActiveElement(submitter);
    const extras = drawer.querySelector('dyld-cart-extras') || new DyldCartExtras();
    extras.drawer = drawer;
    try {
      const success = await extras.changeBoost({
        dataset: { boostVariant: String(variantId) },
        id: 'DyldCartBoostHeading',
        getAttribute: () => 'false',
      }, { selectOnly: true });
      if (success || !drawer.classList.contains('is-empty')) drawer.open(submitter);
      if (!success) {
        const wrapper = form.closest('product-form') || form;
        const error = wrapper.querySelector('.product-form__error-message');
        if (error) {
          error.closest('.product-form__error-message-wrapper').hidden = false;
          error.textContent = extras.lastError || 'Your cart is updating. Please try again.';
        }
      }
    } finally {
      submitter?.removeAttribute('aria-busy');
    }
  }, true);
})();
